import {createApi, fetchBaseQuery} from "@reduxjs/toolkit/query/react";
import type {RootState} from "../app/store";
import type {Template, TemplateWithBody, TestTemplate} from "../types/templates";
import {UpdateTemplate} from "../types/templates";
import {config} from "../config";
import {ResourceId} from "../types/resourceId";

export const templatesApi = createApi({
  reducerPath: "templatesApi",
  baseQuery: fetchBaseQuery({
    baseUrl: config.apiBaseUrl,
    prepareHeaders: (headers, {getState}) => {
      const token = (getState() as RootState)?.auth?.accessToken;
      if (token) headers.set("authorization", `Bearer ${token}`);
      return headers;
    },
  }),
  tagTypes: ["Template"],
  endpoints: (builder) => ({
    duplicateTemplate: builder.mutation<ResourceId, ResourceId>({
      query: (body) => ({
        url: "/templates/duplicate",
        method: "POST",
        body // POST ResourceId object here // fixme
      }),
      invalidatesTags: ['Template'],
    }),

    createTemplate: builder.mutation<ResourceId, TemplateWithBody>({
      query: (body) => ({
        url: "/templates/template",
        method: "POST",
        body,
      }),
      invalidatesTags: ['Template'],
      /*
      instead of invalidatesTags ????
      async onQueryStarted(newPost, {dispatch, queryFulfilled}) {
        const patchResult = dispatch(
          postsApiSlice.util.updateQueryData('getPosts', undefined, (draft) => {
            // Optimistically add the new post to the list
            draft.push(newPost); // Assuming newPost already contains the ID or the server returns it
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo(); // Revert if the POST fails
        }
      },
      */

    }),

    updateTemplate: builder.mutation<null, UpdateTemplate>({
      query: (body) => ({
        url: "/templates/template",
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, arg) => [{type: 'Template', id: arg.id}],
    }),

    getTemplates: builder.query<Template[], void>({
      query: () => "/templates/",
      providesTags: (result) =>
        // is result available?
        result
          ? // successful query
          [
            ...result.map(
              ({id}) => ({type: 'Template', id}) as const), {type: 'Template', id: `LIST`},
          ]
          : // an error occurred, but we still want to refetch this query when `{ type: 'Posts', id: 'LIST' }` is invalidated
          [{type: 'Template', id: 'LIST'}],
    }),

    getTemplate: builder.query<TemplateWithBody, string>({
      query: (id) => `/templates/template/${id}`,
    }),

    deleteTemplate: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `templates/template/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [{type: 'Template', id}],
    }),

    testTemplate: builder.mutation<null, TestTemplate>({
      query: (body) => ({
        url: "/templates/test",
        method: "POST",
        body,
      }),
    }),


  }),
});

export const {
  useTestTemplateMutation,
  useDuplicateTemplateMutation,
  useCreateTemplateMutation,
  useDeleteTemplateMutation,
  useUpdateTemplateMutation,
  useGetTemplatesQuery,
  useGetTemplateQuery,
  useLazyGetTemplateQuery
} = templatesApi;
